import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import {
  EventMessage,
  EventType,
  AuthenticationResult,
  InteractionStatus,
  InteractionRequiredAuthError,
} from '@azure/msal-browser';
import {
  DsHeaderModule,
  DsNavigationBarModule,
  DsImprintModule,
} from '@bmw-ds/components';
import { HeaderComponent } from './header/header.component';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpClientModule,
  HttpHeaders,
} from '@angular/common/http';
import { from, Observable, of } from 'rxjs';
import {
  switchMap,
  map,
  combineLatestWith,
  catchError,
  retry,
  shareReplay,
  tap,
} from 'rxjs/operators';
import { environment } from '../environments/environment';
import { AuthService } from './services/auth.service';
import { Subscription } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { registerLocaleData } from '@angular/common';
import { UserSettingsService } from './services/user-settings.service';
import localeDe from '@angular/common/locales/de';

registerLocaleData(localeDe);

interface UserInfo {
  username: string;
  department?: string;
  profileImg?: string;
}

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterModule,
    DsHeaderModule,
    DsNavigationBarModule,
    // DsImprintModule,
    HeaderComponent,
    CommonModule,
    HttpClientModule, // ✅ Add this line to fix the issue
  ],
  template: `
    <div *ngIf="!isAuthInProgress; else loading">
      <ds-header
        [(localeID)]="localeID"
        [localeOptions]="localeOptions"
        (localeIDChange)="changeLocale($event)"
        appName="Onboarding Buddy"
        [userInfo]="userInfo"
        (requestLogout)="handleLogout()"
      ></ds-header>
      <div class="d-flex">
        <div class="navigation-bar__wrapper position-sticky">
          <ds-navigation-bar
            *ngIf="menuVisible"
            #navBar
            [items]="items"
          ></ds-navigation-bar>
        </div>
        <div class="container-fluid">
          <router-outlet></router-outlet>
        </div>
      </div>
    </div>

    <ng-template #loading>
      <div class="loading-container">
        <div style="margin-left:100px !important; " class="loader"></div>
        <p></p>
      </div>
    </ng-template>
  `,
})
export class AppComponent implements OnInit {
  userInfo: UserInfo | undefined = undefined;
  isAuthInProgress = true;
  isKiosk = false;
  isMeister = false;
  isSelfService = false;
  isAdmin = false;
  localeID = 'en';
  localeOptions = [
    { id: 'en', label: 'en', language: 'English' },
    { id: 'de', label: 'de', language: 'Deutsch' },
  ];

  loadSettingsUrl = environment.azureFunction.orchestratorQEndpoint;
  private hasTriedFetchingProfilePhoto = false;

  private graphEndpoint = 'https://graph.microsoft.com/v1.0/me/memberOf';
  private scopes = ['User.Read', 'GroupMember.Read.All'];
  private userUpdated = false;
  private profileImageUrl: string | null = null;

  private previousSelfServiceState: boolean | null = null;
  private previousKioskState: boolean | null = null;
  private previousMeisterState: boolean | null = null;
  private previousAdminState: boolean | null = null;
  private previousDataStewardState: boolean | null = null; // NEW
  private roleSubscription: Subscription | null = null; // ✅ Store subscription to clean up
  private loadingTimeout: any;
  private settingsLoaded: boolean = false;
  public menuVisible: boolean = false;

  items: any[] = [
    { label: 'Dashboard', routerLink: '/dashboard', icon: 'calendar_settings' },
  ];

  constructor(
    private authService: MsalService,
    private router: Router,
    private msalBroadcastService: MsalBroadcastService,
    private cdRef: ChangeDetectorRef,
    private http: HttpClient,
    private roleService: AuthService,
    private translateService: TranslateService,
    private userSettingsService: UserSettingsService,
  ) {
    // ✅ Detect browser language and set it
    this.translateService.setDefaultLang('en');

    // ✅ Detect browser language and apply it if supported
    const browserLang = this.translateService.getBrowserLang();
    this.localeID = browserLang?.match(/en|de/) ? browserLang : 'en';
    this.translateService.use(this.localeID);
  }

  changeLocale(localeID: string | null) {
    if (localeID) {
      this.localeID = localeID; // ✅ Update the variable
      this.translateService.use(localeID); // ✅ Switch translation
    }
  }
  ngOnInit(): void {
    this.ensureUserIsSet();
    //   // ✅ Handle authentication progress
    //   this.msalBroadcastService.inProgress$.subscribe((status: InteractionStatus) => {
    //     if (status === InteractionStatus.None) {
    //       this.ensureUserIsSet();
    //     }
    //   }
    // );

    // ✅ Handle login success
    this.msalBroadcastService.msalSubject$.subscribe((event: EventMessage) => {
      if (
        event.eventType === EventType.LOGIN_SUCCESS ||
        event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS
      ) {
        const authResult = event.payload as AuthenticationResult;
        this.authService.instance.setActiveAccount(authResult.account);
        setTimeout(() => this.updateUserInfo(), 500);
        this.isAuthInProgress = false;
      }
    });

    // ✅ Subscribe to authentication progress
    this.roleService.kiosk$
      .pipe(
        combineLatestWith(
          this.roleService.kioskEnrollment$, // NEW
          this.roleService.kioskBookings$, // NEW
          this.roleService.meister$,
          this.roleService.selfService$,
          this.roleService.admin$,
          this.roleService.dataSteward$, // NEW
        ),
      )
      .subscribe(
        ([
          kiosk,
          supportEnrollment,
          supportBookings,
          isMeister,
          isSelfService,
          isAdmin,
          isDataSteward,
        ]) => {
          // 🔁 Effective kiosk flag = any of the 3 kiosk roles
          const isKiosk = kiosk || supportEnrollment || supportBookings;

          // ✅ Update menu only if values have changed
          if (
            isKiosk !== this.previousKioskState ||
            isMeister !== this.previousMeisterState ||
            isSelfService !== this.previousSelfServiceState ||
            isAdmin !== this.previousAdminState ||
            isDataSteward !== this.previousDataStewardState
          ) {
            this.previousKioskState = isKiosk;
            this.previousMeisterState = isMeister;
            this.previousSelfServiceState = isSelfService;
            this.previousAdminState = isAdmin;
            this.previousDataStewardState = isDataSteward;

            this.updateMenu(
              isKiosk,
              isMeister,
              isSelfService,
              isAdmin,
              isDataSteward,
            );
          }
        },
      );

    this.loadUserSettings();
  }

  loadUserSettings(): void {
    const account = this.authService.instance.getActiveAccount();
    const email = account?.idTokenClaims?.['preferred_username']?.toLowerCase();

    if (!email || !account) return;

    this.authService.instance
      .acquireTokenSilent({
        scopes: ['user.read'],
        account: account,
      })
      .then((result) => {
        const accessToken = result.accessToken;
        const idTokenClaims = account.idTokenClaims;

        // console.log('roles: ' + JSON.stringify(idTokenClaims?.roles));

        const body = {
          // email: email,
          // msalToken: account.idToken,
        };

        this.http
          .post(this.loadSettingsUrl, body, {
            observe: 'response',
            headers: { Authorization: `Bearer ${account.idToken}` },
          })
          .subscribe({
            next: (response: any) => {
              if (response.body.instanceId) {
                const instanceId = response.body.instanceId;
                this.pollUserSettings(
                  `${environment.pollingBase}${instanceId}`,
                );
              }
            },
            error: (err) => {
              if (err.status) {
              }
            },
          });
      })
      .catch((err) => {
        console.error('❌ Failed to acquire token silently:', err);
      });
  }

  rewriteStatusQueryUri(uri: string): string {
    try {
      const parsedUrl = new URL(uri);
      // console.log(parsedUrl.pathname + parsedUrl.search);
      return parsedUrl.pathname + parsedUrl.search; // strip domain, keep path + query
    } catch (e) {
      console.error('❌ Failed to parse statusQueryUri:', uri, e);
      return uri;
    }
  }

  extractInstanceId(uri: string): string {
    const match = uri.match(/instances\/([^?]+)/);
    // console.log('match: ' + match?.[1] || '');
    return match?.[1] || '';
  }

  // Add this helper inside the same class
  private resolveLocale(preferred?: string): string {
    // Try the user's browser language first when needed
    const browserLocale = (
      navigator.languages?.[0] ||
      navigator.language ||
      'en'
    ).toLowerCase();
    const browserBase = browserLocale.split('-')[0]; // e.g., 'en-US' -> 'en'

    if (!preferred) return browserBase;

    const base = preferred.toLowerCase();
    // If the app explicitly supports only 'en' and 'de', prefer those;
    // otherwise, use the browser's base language.
    if (base === 'en' || base === 'de') return base;

    return browserBase;
  }

  pollUserSettings(statusQueryUri: string, retries = 20, delay = 3000): void {
    let attempts = 0;
    // console.log('polling');
    const poll = setInterval(() => {
      this.http.get(statusQueryUri).subscribe({
        next: (status: any) => {
          if (status.runtimeStatus === 'Completed') {
            clearInterval(poll);
            const result = status.output;

            if (result && result.error !== 'NotFound') {
              if (result.error === 'Unauthorized') {
                console.warn('🔒 Unauthorized access. Forcing re-login...');
                this.authService.logoutRedirect();
                return;
              }

              // console.log('settings: ' + JSON.stringify(result.userSettings));
              this.userSettingsService.setSettings({
                selectedLanguage: result.userSettings?.selectedLanguage || '',
                selectedLocation: result.userSettings?.selectedLocation || '',
                selectedBuilding: result.userSettings?.selectedBuilding || '',
                selectedKiosk: result.userSettings?.selectedKiosk || '',
                selectedRepresentative:
                  result.userSettings?.selectedRepresentative || '',
              });

              // Determine localeID: if not 'en' or 'de', fall back to browser language
              const preferredLang =
                this.userSettingsService.selectedLanguage || '';
              const localeID = this.resolveLocale(preferredLang);

              this.translateService.use(localeID);
              this.settingsLoaded = true;
            } else {
              console.warn('⚠️ No user settings found.');
              // No settings at all → use browser language as default
              const localeID = this.resolveLocale();
              this.translateService.use(localeID);
              this.settingsLoaded = true; // Unblock UI even if settings were missing
            }
          } else if (status.runtimeStatus === 'Failed' || attempts >= retries) {
            clearInterval(poll);
            console.error('❌ Orchestrator failed or timed out.');
          }

          attempts++;
        },
        error: (err) => {
          clearInterval(poll);
          console.error('❌ Error polling orchestrator:', err);
        },
      });
    }, delay);
  }

  ngOnDestroy(): void {
    if (this.roleSubscription) {
      this.roleSubscription.unsubscribe();
    }
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    this.clearCachedProfileImage();
  }

  // Add this helper method
  private forceLogout(): void {
    this.authService.logout(); // or msalService.logoutRedirect() if needed
  }

  settings: string = '';
  history: string = '';
  // ✅ Update header menu dynamically
  updateMenu(
    isKiosk: boolean,
    isMeister: boolean,
    isSelfService: boolean,
    isAdmin: boolean,
    isDataSteward: boolean,
  ): void {
    this.settings = this.translateService.instant('MENU_SETTINGS');

    this.history = this.translateService.instant('MENU_HISTORY');

    this.items = [];

    // ✅ Conditionally add menu items

    this.items = [
      {
        label: 'Dashboard',
        routerLink: '/dashboard',
        icon: 'calendar_settings',
      },
      ...(isSelfService
        ? [
            {
              label: 'Self-Service',
              routerLink: '/self-service',
              icon: 'contacts',
            },
          ]
        : []),
      ...(isKiosk || isDataSteward
        ? [{ label: 'Kiosk', routerLink: '/kiosk', icon: 'contacts_book' }]
        : []),
      ...(isMeister || isDataSteward
        ? [{ label: 'Meister', routerLink: '/meister', icon: 'group_favorite' }]
        : []),
      { label: this.history, routerLink: '/history', icon: 'history' },
      { label: this.settings, routerLink: '/settings', icon: 'settings' },
      ...(isAdmin || isDataSteward
        ? [{ label: 'Admin', routerLink: '/admin', icon: 'dataprotection' }]
        : []),
    ];
    setTimeout(() => {
      // code to run after 0.5 seconds
      this.menuVisible = true;
    }, 700); // time in milliseconds

    // if (isMeister) {
    //   this.items.push({
    //     label: 'Meister Features',
    //     group: true,
    //     children: [
    //       { label: 'Meister', routerLink: '/meister', icon: 'community' }
    //     ],
    //   });
    // }
  }

  // updateUserInfo(): void {
  //   setTimeout(() => {
  //     const account = this.authService.instance.getActiveAccount() || undefined;

  //     if (!account) {
  //       this.userInfo = undefined;
  //       return;
  //     }

  //     // First get the profile photo
  //     this.getUserProfilePhotoUrl().subscribe(photoUrl => {
  //       // Then fetch user group IDs
  //       this.getUserGroupIds().subscribe(groupIds => {
  //         const isKiosk = groupIds.some(id => environment.groupConfig.kioskGroups.includes(id));
  //         const isMeister = groupIds.some(id => environment.groupConfig.meisterGroups.includes(id));

  //         this.userInfo = {
  //           username: account.name || 'Unknown User',
  //           department: (account.idTokenClaims?.['department'] as string) || 'Unknown Department',
  //           profileImg: photoUrl || `https://www.gravatar.com/avatar?d=identicon`, // Fallback if no photo
  //         };

  //         this.roleService.updateUserRoles(isKiosk, isMeister);
  //         this.cdRef.detectChanges();
  //       });
  //     });

  //     this.authService.instance.setActiveAccount(account);
  //   }, 500);
  // }

  private isUpdatingUserInfo = false;
  private profilePhoto$: Observable<string | null> | null = null;

  updateUserInfo(): void {
    if (this.isUpdatingUserInfo) return;

    this.isUpdatingUserInfo = true;

    setTimeout(() => {
      const account = this.authService.instance.getActiveAccount() || undefined;

      if (!account) {
        this.userInfo = undefined;
        this.isUpdatingUserInfo = false;
        return;
      }

      this.authService.instance.setActiveAccount(account);

      this.getUserProfilePhotoUrl().subscribe({
        next: (photoUrl) => {
          const roles: string[] = account.idTokenClaims?.['roles'] || [];

          const isKiosk = roles.includes('Support.All');
          const isKioskEnrollment = roles.includes('Support.Enrollment');
          const isKioskBookings = roles.includes('Support.Booking');
          const isMeister = roles.includes('Manager.All');
          const isSelfService = roles.includes('SelfService.All');
          const isAdmin = roles.includes('Admin.All');
          const isDataSteward = roles.includes('Admin.DataSteward');

          this.userInfo = {
            username: account.name || 'Unknown User',
            department: (account.idTokenClaims?.['department'] as string) || '',
            profileImg: photoUrl || `/assets/avatar.png`,
          };

          // ✅ Update roles in AuthService
          this.roleService.updateUserRoles(
            isKiosk,
            isMeister,
            isSelfService,
            isAdmin,
            isKioskEnrollment, // Support.Enrollment
            isKioskBookings, // Support.Bookings
            isDataSteward,
          );

          this.cdRef.detectChanges();
          this.isUpdatingUserInfo = false;
        },
        error: (err) => {
          // console.error('Failed to update user info:', err);
          this.isUpdatingUserInfo = false;
        },
      });
    }, 500);
  }

  getUserProfilePhotoUrl(): Observable<string | null> {
    if (this.profileImageUrl) return of(this.profileImageUrl);
    if (this.profilePhoto$) return this.profilePhoto$; // Return cached observable

    const account = this.authService.instance.getActiveAccount() || undefined;
    if (!account) return of(null);

    this.profilePhoto$ = from(
      this.authService.instance.acquireTokenSilent({
        account,
        scopes: this.scopes,
      }),
    ).pipe(
      switchMap((authResult: AuthenticationResult) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${authResult.accessToken}`,
        });

        return this.http.get(
          'https://graph.microsoft.com/v1.0/me/photo/$value',
          {
            headers,
            responseType: 'blob',
          },
        );
      }),
      retry(2),
      map((imageBlob: Blob) => {
        this.profileImageUrl = URL.createObjectURL(imageBlob);
        return this.profileImageUrl;
      }),
      catchError((err) => {
        // console.warn('Could not fetch profile photo after 3 attempts, using default...', err);
        return of(null);
      }),
      shareReplay(1), // ✅ Cache result for future subscribers
    );

    return this.profilePhoto$;
  }

  clearCachedProfileImage(): void {
    if (this.profileImageUrl) {
      URL.revokeObjectURL(this.profileImageUrl);
      this.profileImageUrl = null;
    }
  }

  getUserGroupIds(): Observable<string[]> {
    const account = this.authService.instance.getActiveAccount() || undefined;

    if (!account) {
      // console.error('No active account found...');
      return new Observable<string[]>((observer) => observer.next([]));
    }

    return from(
      this.authService.instance.acquireTokenSilent({
        account: account,
        scopes: this.scopes,
      }),
    ).pipe(
      switchMap((authResult: AuthenticationResult) => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${authResult.accessToken}`,
          'Content-Type': 'application/json',
        });

        return this.http.get<{ value: any[] }>(this.graphEndpoint, { headers });
      }),
      map((response) => response.value.map((group) => group.id)),
      // tap(groupIds => // console.log('User group IDs:', groupIds)) // <--- logs once per subscription
      tap(),
    );
  }

  ensureUserIsSet(): void {
    let account = this.authService.instance.getActiveAccount();

    if (!account) {
      const accounts = this.authService.instance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        this.authService.instance.setActiveAccount(account);
      }
    }

    if (account) {
      // Attempt silent token acquisition to verify session is still valid
      this.authService.instance
        .acquireTokenSilent({
          scopes: ['user.read'], // Replace with your actual scopes
          account: account,
        })
        .then(() => {
          this.updateUserInfo();
        })
        .catch((error) => {
          if (error instanceof InteractionRequiredAuthError) {
            // console.warn('Session is invalid. Redirecting to login.');
            this.authService.logout(); // or loginRedirect()
          } else {
            // console.error('Unexpected token error:', error);
          }
        });
    } else {
      // console.warn('No account found. Redirecting to login.');
      this.authService.loginRedirect();
    }
  }

  handleLogout(): void {
    this.authService.logoutRedirect();
    this.userInfo = undefined;
    this.isAuthInProgress = true;
  }
}
